import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

function parseActiveStatus(v: unknown): boolean {
  const direct = bool(v);
  if (direct != null) return direct;
  if (typeof v === 'string') {
    const normalized = v.trim().toLowerCase();
    if (normalized === 'true' || normalized === 'active' || normalized === '1') return true;
    if (normalized === 'false' || normalized === 'inactive' || normalized === '0') return false;
  }
  if (typeof v === 'number') {
    if (v === 1) return true;
    if (v === 0) return false;
  }
  return true;
}

export const CASE_SCENARIO_TITLE_MAX_LENGTH = 100;
export const CASE_SCENARIO_DESCRIPTION_MAX_LENGTH = 255;

export type CaseScenarioDocument = {
  documentId: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
};

export type CaseScenarioListRow = {
  id: string;
  title: string;
  description: string;
  active: boolean;
  updatedAt: string;
};

export type CaseScenarioDetail = {
  id: string;
  title: string;
  description: string;
  active: boolean;
  document: CaseScenarioDocument | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type CaseScenarioInfoRow = {
  id: string;
  title: string;
  description: string;
  document: CaseScenarioDocument;
};

export type CaseScenarioListParams = {
  page: number;
  pageSize: number;
  title?: string;
  /** When omitted, list includes all statuses. */
  status?: boolean;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  signal?: AbortSignal;
};

export type CaseScenarioListResult = {
  items: CaseScenarioListRow[];
  total: number;
  totalPages: number;
};

export type SaveCaseScenarioUploadedFile = {
  fileName: string;
  originalFileName: string;
  documentTypeId?: string;
  reason?: string;
};

export type SaveCaseScenarioInput = {
  title: string;
  description?: string;
  active: boolean;
  uploadedFile?: SaveCaseScenarioUploadedFile;
  removeDocument?: boolean;
};

type ApiRow = Record<string, unknown>;

function pickStr(o: ApiRow, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function pickNum(o: ApiRow, ...keys: string[]): number {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 0;
}

function unwrapRow(data: unknown): ApiRow {
  if (!data || typeof data !== 'object') return {};
  const record = data as ApiRow;
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as ApiRow;
  }
  return record;
}

function unwrapRows(data: unknown): ApiRow[] {
  if (Array.isArray(data)) return data as ApiRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as ApiRow;
  if (Array.isArray(record.content)) return record.content as ApiRow[];
  if (Array.isArray(record.data)) return record.data as ApiRow[];
  if (Array.isArray(record.items)) return record.items as ApiRow[];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as ApiRow;
    if (Array.isArray(inner.content)) return inner.content as ApiRow[];
    if (Array.isArray(inner.data)) return inner.data as ApiRow[];
  }
  return [];
}

function pageMeta(data: unknown): { total: number; totalPages: number } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const record = data as ApiRow;
  const sources = [record];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    sources.push(nested as ApiRow);
  }

  for (const source of sources) {
    const total =
      typeof source.totalElements === 'number'
        ? source.totalElements
        : typeof source.total === 'number'
          ? source.total
          : null;
    const totalPages =
      typeof source.totalPages === 'number' && source.totalPages > 0 ? source.totalPages : null;
    if (total != null) {
      return {
        total,
        totalPages: totalPages ?? Math.max(1, Math.ceil(total / 1)),
      };
    }
  }
  return null;
}

function mapDocument(source: ApiRow): CaseScenarioDocument | null {
  const nested = source.document;
  const doc =
    nested && typeof nested === 'object' && !Array.isArray(nested) ? (nested as ApiRow) : source;

  const fileName = pickStr(doc, 'file_name', 'fileName');
  if (!fileName) return null;

  return {
    documentId: pickStr(doc, 'document_id', 'documentId'),
    fileName,
    originalFileName: pickStr(doc, 'original_file_name', 'originalFileName') || fileName,
    fileSize: pickNum(doc, 'file_size', 'fileSize'),
  };
}

function mapListRow(row: ApiRow): CaseScenarioListRow | null {
  const id = pickStr(row, 'id', 'case_scenario_id', 'caseScenarioId');
  const title = pickStr(row, 'title');
  if (!id || !title) return null;

  return {
    id,
    title,
    description: pickStr(row, 'description'),
    active: parseActiveStatus(row.status ?? row.active),
    updatedAt: pickStr(row, 'last_updated_at', 'lastUpdatedAt', 'updated_at', 'updatedAt'),
  };
}

function mapDetail(row: ApiRow): CaseScenarioDetail | null {
  const id = pickStr(row, 'id', 'case_scenario_id', 'caseScenarioId');
  const title = pickStr(row, 'title');
  if (!id || !title) return null;

  return {
    id,
    title,
    description: pickStr(row, 'description'),
    active: parseActiveStatus(row.active ?? row.status),
    document: mapDocument(row),
    createdAt: pickStr(row, 'created_at', 'createdAt'),
    createdBy: pickStr(row, 'created_by_name', 'createdByName', 'created_by', 'createdBy'),
    updatedAt: pickStr(row, 'last_updated_at', 'lastUpdatedAt', 'updated_at', 'updatedAt'),
    updatedBy: pickStr(
      row,
      'updated_by_name',
      'updatedByName',
      'last_updated_by_name',
      'lastUpdatedByName',
      'updated_by',
      'updatedBy',
      'last_updated_by',
      'lastUpdatedBy',
    ),
  };
}

function mapInfoRow(row: ApiRow): CaseScenarioInfoRow | null {
  const id = pickStr(row, 'id', 'case_scenario_id', 'caseScenarioId');
  const title = pickStr(row, 'title');
  const document = mapDocument(row);
  if (!id || !title || !document) return null;

  return {
    id,
    title,
    description: pickStr(row, 'description'),
    document,
  };
}

function parseTitleList(data: unknown): string[] {
  if (Array.isArray(data)) {
    return data
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim());
  }

  const unwrapped = unwrapRow(data);
  if (Array.isArray(unwrapped.titles)) {
    return (unwrapped.titles as unknown[])
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim());
  }

  const fromRows = unwrapRows(data)
    .map((row) => pickStr(row, 'title'))
    .filter(Boolean);
  if (fromRows.length) return fromRows;

  return [];
}

function parsePage(data: unknown, page: number, pageSize: number): CaseScenarioListResult {
  const rows = unwrapRows(data)
    .map(mapListRow)
    .filter((row): row is CaseScenarioListRow => row != null);

  const meta = pageMeta(data);
  if (meta) {
    const totalPages =
      meta.totalPages > 0
        ? meta.totalPages
        : Math.max(1, Math.ceil(meta.total / Math.max(1, pageSize)));
    return { items: rows, total: meta.total, totalPages };
  }

  const total = rows.length;
  const start = page * pageSize;
  return {
    items: rows.slice(start, start + pageSize),
    total,
    totalPages: Math.max(1, Math.ceil(total / Math.max(1, pageSize))),
  };
}

function buildSearchBody(params: CaseScenarioListParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const title = params.title?.trim();
  const sortBy = params.sortBy?.trim();
  const sortDir = params.sortDir?.trim();

  if (title) body.title = title;
  if (typeof params.status === 'boolean') body.status = params.status;
  if (sortBy) body.sort_by = sortBy;
  if (sortDir) body.sort_direction = sortDir;

  return body;
}

function resolveDocumentTypeId(explicit?: string): string | undefined {
  const fromInput = explicit?.trim();
  if (fromInput) return fromInput;
  const fromEnv = (import.meta.env.VITE_CASE_SCENARIO_DOCUMENT_TYPE_ID ?? '').trim();
  return fromEnv || undefined;
}

function buildSaveBody(input: SaveCaseScenarioInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    title: input.title.trim(),
    description: input.description?.trim() ?? '',
    active: input.active,
  };

  if (input.removeDocument) {
    body.remove_document = true;
  }

  if (input.uploadedFile?.fileName?.trim()) {
    const uploaded: Record<string, unknown> = {
      file_name: input.uploadedFile.fileName.trim(),
      original_file_name:
        input.uploadedFile.originalFileName?.trim() || input.uploadedFile.fileName.trim(),
    };
    const documentTypeId = resolveDocumentTypeId(input.uploadedFile.documentTypeId);
    if (documentTypeId) uploaded.document_type_id = documentTypeId;
    const reason = input.uploadedFile.reason?.trim();
    if (reason) uploaded.reason = reason;
    body.uploaded_file = uploaded;
  }

  return body;
}

/** POST /v1/masters/case-scenarios/search — CMS-CS-1 */
export async function searchCaseScenarios(
  params: CaseScenarioListParams,
): Promise<ApiResult<CaseScenarioListResult>> {
  const page = Math.max(0, params.page);
  const pageSize = Math.max(1, params.pageSize);
  const qs = new URLSearchParams({
    page: String(page),
    size: String(pageSize),
  });

  const res = await caseApiRequest<unknown>(
    `/v1/masters/case-scenarios/search?${qs.toString()}`,
    {
      method: 'POST',
      body: buildSearchBody(params),
      signal: params.signal,
    },
  );

  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: parsePage(res.data, page, pageSize) };
}

/** GET /v1/masters/case-scenarios/titles — CMS-CS-2 */
export async function listCaseScenarioTitles(input?: {
  title?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<string[]>> {
  const qs = new URLSearchParams();
  const title = input?.title?.trim();
  if (title) qs.set('title', title);

  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await caseApiRequest<unknown>(`/v1/masters/case-scenarios/titles${suffix}`, {
    method: 'GET',
    signal: input?.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: parseTitleList(res.data) };
}

/** GET /v1/masters/case-scenarios/{caseScenarioId} — CMS-CS-3 */
export async function getCaseScenarioById(id: string): Promise<ApiResult<CaseScenarioDetail>> {
  const caseScenarioId = id.trim();
  if (!caseScenarioId) return { success: false, message: 'Case scenario id is missing.' };

  const res = await caseApiRequest<unknown>(
    `/v1/masters/case-scenarios/${encodeURIComponent(caseScenarioId)}`,
    { method: 'GET' },
  );
  if (!res.ok) return { success: false, message: res.message };

  const detail = mapDetail(unwrapRow(res.data));
  if (!detail) return { success: false, message: 'Case scenario details are unavailable.' };
  return { success: true, data: detail };
}

/** POST /v1/masters/case-scenarios — CMS-CS-4 */
export async function createCaseScenario(
  input: SaveCaseScenarioInput,
): Promise<ApiResult<CaseScenarioDetail>> {
  const res = await caseApiRequest<unknown>('/v1/masters/case-scenarios', {
    method: 'POST',
    body: buildSaveBody(input),
  });
  if (!res.ok) return { success: false, message: res.message };

  const detail = mapDetail(unwrapRow(res.data));
  if (!detail) return { success: false, message: 'Case scenario was created but details are unavailable.' };
  return { success: true, data: detail };
}

/** PUT /v1/masters/case-scenarios/{caseScenarioId} — CMS-CS-5 */
export async function updateCaseScenario(
  id: string,
  input: SaveCaseScenarioInput,
): Promise<ApiResult<CaseScenarioDetail>> {
  const caseScenarioId = id.trim();
  if (!caseScenarioId) return { success: false, message: 'Case scenario id is missing.' };

  const res = await caseApiRequest<unknown>(
    `/v1/masters/case-scenarios/${encodeURIComponent(caseScenarioId)}`,
    {
      method: 'PUT',
      body: buildSaveBody(input),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const detail = mapDetail(unwrapRow(res.data));
  if (!detail) return { success: false, message: 'Case scenario was updated but details are unavailable.' };
  return { success: true, data: detail };
}

/** DELETE /v1/masters/case-scenarios/{caseScenarioId}/document — CMS-CS-6 */
export async function removeCaseScenarioDocument(
  id: string,
): Promise<ApiResult<CaseScenarioDetail>> {
  const caseScenarioId = id.trim();
  if (!caseScenarioId) return { success: false, message: 'Case scenario id is missing.' };

  const res = await caseApiRequest<unknown>(
    `/v1/masters/case-scenarios/${encodeURIComponent(caseScenarioId)}/document`,
    { method: 'DELETE' },
  );
  if (!res.ok) return { success: false, message: res.message };

  const detail = mapDetail(unwrapRow(res.data));
  if (!detail) return { success: false, message: 'Document was removed but details are unavailable.' };
  return { success: true, data: detail };
}

/** GET /v1/case-scenarios/active — CMS-CS-7 */
export async function listActiveCaseScenariosForInfo(input?: {
  signal?: AbortSignal;
}): Promise<ApiResult<CaseScenarioInfoRow[]>> {
  const res = await caseApiRequest<unknown>('/v1/case-scenarios/active', {
    method: 'GET',
    signal: input?.signal,
  });
  if (!res.ok) return { success: false, message: res.message };

  const rows = unwrapRows(res.data)
    .map(mapInfoRow)
    .filter((row): row is CaseScenarioInfoRow => row != null);
  return { success: true, data: rows };
}
